import React, { useState, useRef, useCallback } from "react";
import { v4 as uuid } from "uuid";
import SignaturePadContainer from "./SignaturePad";
import { useTheme, lighten } from "@mui/material/styles";
import { Dialog, DialogContent, Button, Typography, Grid } from "@mui/material";
import DialogTitle from "../DialogTitle/DialogTitle";
import SignaturePad from "react-signature-pad-wrapper";
import { useTranslation } from "react-i18next";
import { StyledDialogActions } from "./styles";
import { Title } from "../DialogTitle/styles";

interface Props {
  title: string;
  text: string;
  buttonTitle: string;
  submitButtonTitle?: string;
  submitButtonStyle?: React.CSSProperties;
  handleSignature?: (signature: File, type: string) => void;
  children?: any;
  openFromTheStart?: boolean;
  additionalActions?: JSX.Element;
  wrapInDialog?: boolean;
  handleCancel?: () => void;
  alertWhenEmpty?: string;
  disabled?: boolean;
}
interface State {
  open: boolean;
}

const SignaturePadWrapper = ({
  title,
  text,
  buttonTitle,
  submitButtonTitle,
  submitButtonStyle,
  handleSignature,
  children,
  openFromTheStart,
  additionalActions,
  wrapInDialog,
  handleCancel,
  alertWhenEmpty,
  disabled = false,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const sigCanvas = useRef<SignaturePad>(null);
  const tRP = "components.signature_pad";
  const [state, setState] = useState<State>({
    open: openFromTheStart ?? false,
  });

  const submit = useCallback(async () => {
    if (sigCanvas.current?.isEmpty()) {
      alert(alertWhenEmpty ?? "Please draw your signature");
    } else {
      const blob = await fetch(sigCanvas.current?.toDataURL("image/svg+xml") ?? "").then((r) => r.blob());

      handleCancel
        ? handleCancel()
        : setState((state) => ({
            ...state,
            open: false,
          }));

      handleSignature && handleSignature(new File([blob], `sig_${uuid()}.svg`), "image/svg+xml");
    }
  }, [handleSignature]);

  const clear = useCallback(() => {
    !!sigCanvas.current && sigCanvas.current.clear();
  }, []);

  const cancel = useCallback(async () => {
    setState((state) => ({
      ...state,
      open: false,
    }));
  }, []);

  return (
    <>
      {wrapInDialog ? (
        <>
          {children ? (
            <div onClick={() => !disabled && setState((state) => ({ ...state, open: true }))}>{children}</div>
          ) : (
            <Button
              title={buttonTitle}
              variant="contained"
              onClick={() => !disabled && setState((state) => ({ ...state, open: true }))}
            >
              <Typography>{buttonTitle}</Typography>
            </Button>
          )}
          <Dialog open={state.open}>
            <DialogTitle
              title={title}
              handleClose={() => {
                setState((state) => ({ ...state, open: false }));
              }}
            />
            <DialogContent>
              {additionalActions}
              <SignaturePadContainer
                text={text}
                refOBj={sigCanvas}
              />
            </DialogContent>
            <StyledDialogActions>
              <Button onClick={cancel}>{t(`common_words.cancel`)}</Button>
              <Button onClick={clear}>{`${t(`${tRP}.clear`)}`}</Button>
              <Button
                onClick={submit}
                variant={"contained"}
                style={{
                  ...submitButtonStyle,
                }}
              >
                {submitButtonTitle ?? `${t(`buttons.submit`)}`}
              </Button>
            </StyledDialogActions>
          </Dialog>
        </>
      ) : (
        <Grid style={{ display: "flex", flexDirection: "column" }}>
          <Title style={{ padding: "0px" }}>{title}</Title>
          {additionalActions}
          <SignaturePadContainer
            text={text}
            refOBj={sigCanvas}
          />
          <Grid
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button onClick={handleCancel}>{t(`common_words.cancel`)}</Button>
            <Button onClick={clear}>{`${t(`${tRP}.clear`)}`}</Button>
            <Button
              onClick={submit}
              variant={"contained"}
              style={{
                ...submitButtonStyle,
              }}
            >
              {submitButtonTitle ?? `${t(`buttons.submit`)}`}
            </Button>
          </Grid>
        </Grid>
      )}
    </>
  );
};
export default SignaturePadWrapper;
